import type { components } from '../../lib/api/schema.gen'
import { addAmounts, formatMoney } from './cart-money'
import { formatLongWeekdayDate } from './long-weekday-date'

type LineItem = components['schemas']['LineItem']
type CartLinePresentation = components['schemas']['CartLinePresentation']
type SourceableKind = components['schemas']['SourceableKind']

/**
 * UID-96 (C1) — the three readiness buckets the prototype's "Ship as ready"
 * split view groups shipments by (STANDARD / CONFIGURABLE / CUSTOM shipment
 * cards, `Fictiv Platform.dc.html` `_splitDefs`/`splitCards`). No new backend
 * field is needed for this: it's derived from the existing, already-frozen
 * `Sourceable.kind` (`catalog` | `configurable` | `custom` | `assembly_bom`)
 * carried on every `LineItem.sourceable` — the same seam `materialDescription`
 * in `cart-derivation.ts` already switches on.
 */
export type ReadinessBucket = 'standard' | 'configurable' | 'custom'

/** Bucket display order, matching the prototype's `order=['standard','configurable','custom']`. */
const BUCKET_ORDER: readonly ReadinessBucket[] = ['standard', 'configurable', 'custom']

/** Full weekday names for the shipment-card headline date (UID-410 AC3) —
 * distinct from the shared `shipDateLabel` short form (`cart-derivation.ts`)
 * used by cart-line rows and the order-summary "Arrives … · One shipment"
 * string, which have their own passing tests and must NOT change here. This
 * formatter is local to this module and derives from the raw ISO `shipDate`,
 * never from the pre-formatted short label. */
/** e.g. "2026-08-19" -> "Wednesday, Aug 19". */
function formatLongWeekdayLabel(isoDate: string): string | null {
  const d = new Date(`${isoDate}T00:00:00Z`)
  if (Number.isNaN(d.getTime())) {
    return null
  }
  return formatLongWeekdayDate(d)
}

/**
 * `catalog` → standard (in-stock/local), `configurable` → configurable
 * (auto-priced parametric), `custom` and `assembly_bom` → custom (made-to-
 * order — a multi-part BOM is, like a custom part, never off-the-shelf).
 */
const BUCKET_BY_KIND: Readonly<Record<SourceableKind, ReadinessBucket>> = {
  catalog: 'standard',
  configurable: 'configurable',
  custom: 'custom',
  assembly_bom: 'custom',
}

export function deriveReadinessBucket(kind: SourceableKind): ReadinessBucket {
  return BUCKET_BY_KIND[kind]
}

export interface ShipmentGroup {
  readonly bucket: ReadinessBucket
  /** e.g. "Shipment 1", "Shipment 2" — a plain per-shipment ordinal (no
   * "of N" suffix). */
  readonly shipmentLabel: string
  /** e.g. "Wednesday, Aug 19" (long weekday, no "Arrives" prefix — UID-410
   * AC3), or a pending placeholder when no line in the bucket has a ship
   * date yet. */
  readonly arriveLabel: string
  readonly lineCount: number
  readonly unitCount: number
  /** Decimal-string subtotal for this bucket (Σ line subtotals), for callers
   * that need the raw amount (e.g. the custom region-tier price ratios). */
  readonly subtotalAmount: string
  readonly currency: string
  readonly subtotalLabel: string
  readonly lines: readonly LineItem[]
}

/**
 * UID-96 (C1) — group a "Ship as ready" cart's lines into one card per
 * readiness bucket present, each labelled "Shipment N of M" (M = the number
 * of buckets actually present, not a fixed 3) with its own arrival date and
 * subtotal — the multi-shipment breakdown the prototype shows instead of only
 * per-row ship dates.
 */
export function deriveShipmentGroups(
  lines: readonly LineItem[],
  presentationsBySourceableId: ReadonlyMap<string, CartLinePresentation>,
): ShipmentGroup[] {
  const byBucket = new Map<ReadinessBucket, LineItem[]>()
  for (const line of lines) {
    const bucket = deriveReadinessBucket(line.sourceable.kind)
    const existing = byBucket.get(bucket)
    if (existing) {
      existing.push(line)
    } else {
      byBucket.set(bucket, [line])
    }
  }

  const presentBuckets = BUCKET_ORDER.filter((bucket) => (byBucket.get(bucket)?.length ?? 0) > 0)

  return presentBuckets.map((bucket, index) => {
    const bucketLines = byBucket.get(bucket) ?? []
    const presentations = bucketLines
      .map((line) => presentationsBySourceableId.get(line.sourceable.id))
      .filter((p): p is CartLinePresentation => p !== undefined)

    // QA UID-96 defect fix: a shipment can't leave before every line in its
    // bucket is ready, so the card's arrival date must be the LATEST
    // (governing) ship date among the bucket's lines — not whichever line
    // happens to be first in `lines` array order (the prior bug). ISO
    // (`YYYY-MM-DD`) `shipDate` strings compare correctly lexicographically.
    //
    // UID-617: `presentation.shipDate` reflects only the cart's CURRENTLY
    // ACTIVE `shipPlan.mode` — in `ship_together` mode every line's
    // presentation collapses onto the one shared arrival date, so a caller
    // previewing the "ship as ready" breakdown while `ship_together` is
    // still selected (checkout's DeliveryPlanStep shows BOTH cards at once,
    // regardless of which is active) would see every bucket read "Arrival
    // date pending" even though each line's own kind-based date is right
    // there. Fall back to the line's OWN `deliveryPromise.committedDates[0]`
    // — always present regardless of which mode is currently active — so
    // the preview is correct independent of the cart's live selection.
    const candidateDates = bucketLines.map(
      (line) =>
        presentationsBySourceableId.get(line.sourceable.id)?.shipDate ??
        line.deliveryPromise?.committedDates?.[0] ??
        null,
    )
    const governingDate = candidateDates
      .filter((date): date is string => date != null)
      .reduce<string | undefined>(
        (latest, date) => (latest === undefined || date > latest ? date : latest),
        undefined,
      )
    const governingLongDateLabel = governingDate ? formatLongWeekdayLabel(governingDate) : null
    const currency = presentations.find((p) => p.currency)?.currency ?? 'USD'
    const subtotalAmounts = presentations
      .map((p) => p.lineSubtotalAmount)
      .filter((amount): amount is string => amount != null)
    const unitCount = bucketLines.reduce((sum, line) => sum + line.quantity.value, 0)
    const subtotalAmount = subtotalAmounts.length > 0 ? addAmounts(subtotalAmounts) : '0.00'

    return {
      bucket,
      shipmentLabel: `Shipment ${index + 1}`,
      arriveLabel: governingLongDateLabel ?? 'Arrival date pending',
      lineCount: bucketLines.length,
      unitCount,
      subtotalAmount,
      currency,
      subtotalLabel: formatMoney(subtotalAmount, currency),
      lines: bucketLines,
    }
  })
}
